import { treeViewAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const depthOffsetStyle = {
  '--tree-depth': 'calc(var(--depth) - 1)',
  '--tree-indentation-offset': 'calc(var(--tree-indentation) * var(--tree-depth))',
  '--tree-icon-offset': 'calc(var(--tree-icon-size) * var(--tree-depth) * 0.5)',
  '--tree-offset': 'calc(var(--tree-padding-inline-start) + var(--tree-indentation-offset) + var(--tree-icon-offset))',
  paddingInlineStart: 'var(--tree-offset)',
} as const;

const baseItemStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--tree-item-gap)',
  cursor: 'pointer',
  userSelect: 'none',
  position: 'relative',
  ...depthOffsetStyle,
  focusVisibleRing: 'inside',
  focusRingWidth: '2px',
  _hover: {
    background: 'background/secondary',
    _active: {
      background: 'background/tertiary',
    },
  },
  _active: {
    background: 'background/tertiary',
  },
  _selected: {
    background: 'background/selected',
    _hover: {
      background: 'background/selected-hover',
      _active: {
        background: 'background/selected-hover',
      },
    },
    _active: {
      background: 'background/selected-hover',
    },
  },
  // Colours, not opacity: the row's actions stay usable on a disabled (empty) node and must not fade with it.
  _disabled: {
    cursor: 'not-allowed',
  },
} as const;

const baseTextStyle = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  _disabled: {
    color: 'text/tertiary',
  },
} as const;

const treeViewSlotRecipe = defineSlotRecipe({
  className: 'bitkit-tree-view',
  slots: [...treeViewAnatomy.keys(), 'actionGroup', 'footer', 'suffixGroup', 'secondaryText'],
  base: {
    root: {
      width: 'full',
      display: 'flex',
      flexDirection: 'column',
    },
    tree: {
      display: 'flex',
      flexDirection: 'column',
      textStyle: 'body/md/regular',
      '--tree-item-gap': 'spacing.8',
      '--tree-indentation': 'spacing.16',
      '--tree-padding-inline-start': 'spacing.8',
      '--tree-icon-size': 'spacing.16',
      _icon: {
        boxSize: 'var(--tree-icon-size)',
      },
    },
    label: {
      fontWeight: 'medium',
      textStyle: 'body/sm/regular',
    },
    branch: {
      position: 'relative',
    },
    branchContent: {
      position: 'relative',
      overflow: 'hidden',
      _open: {
        animationName: 'expand-height, fade-in',
        animationDuration: 'moderate',
      },
      _closed: {
        animationName: 'collapse-height, fade-out',
        animationDuration: 'moderate',
      },
    },
    branchIndicator: {
      display: 'flex',
      justifyContent: 'center',
      color: 'icon/secondary',
      _disabled: {
        color: 'icon/disabled',
      },
    },
    branchTrigger: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    branchControl: {
      ...baseItemStyle,
    },
    item: {
      ...baseItemStyle,
      // Direct-child selector (not Chakra's _icon): the item also hosts SVGs in its suffix and
      // action slots, which keep their own colour.
      '& > svg': {
        color: 'icon/secondary',
      },
      _active: {
        ...baseItemStyle._active,
        '& > svg': {
          color: 'icon/primary',
        },
      },
      _selected: {
        ...baseItemStyle._selected,
        '& > svg': {
          color: 'icon/primary',
        },
      },
      _disabled: {
        ...baseItemStyle._disabled,
        '& > svg': {
          color: 'icon/disabled',
        },
      },
    },
    branchText: baseTextStyle,
    itemText: baseTextStyle,
    branchIndentGuide: {},
    itemIndicator: {},
    nodeCheckbox: {
      display: 'inline-flex',
    },
    suffixGroup: {
      display: 'flex',
      gap: '8',
      alignItems: 'center',
      justifyContent: 'flex-end',
      color: 'text/secondary',
      '[data-selected] > &, [data-state="open"] > &': {
        color: 'text/primary',
      },
      paddingInlineStart: '16',
      flexShrink: 0,
      _groupHover: {
        display: 'none',
      },
    },
    footer: depthOffsetStyle,
    actionGroup: {
      display: 'none',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingInlineStart: '16',
      flexShrink: 0,
      _groupHover: {
        display: 'flex',
      },
    },
    secondaryText: {
      textStyle: 'body/sm/regular',
      color: 'text/secondary',
    },
  },
  variants: {
    variant: {
      files: {
        branchControl: {
          borderRadius: '4',
          paddingInlineEnd: '16',
          '& > svg': {
            marginBlockStart: '8',
          },
          '& > .badge': {
            marginBlockStart: '6',
          },
        },
        item: {
          borderRadius: '4',
          paddingInlineEnd: '16',
          '& > svg': {
            marginBlockStart: '8',
          },
          '& > .badge': {
            marginBlockStart: '6',
          },
        },
        footer: {
          paddingInlineEnd: '16',
        },
        branchIndicator: {
          alignItems: 'flex-start',
          marginBlockStart: '8',
          alignSelf: 'stretch',
          position: 'relative',
          _open: {
            '&::after': {
              content: '""',
              position: 'absolute',
              insetBlockStart: '16',
              insetBlockEnd: 0,
              insetInlineStart: '50%',
              width: '1',
              background: 'border/regular',
            },
          },
        },
        branchText: {
          paddingBlock: '6',
        },
        itemText: {
          paddingBlock: '6',
        },
        suffixGroup: {
          height: rem(31),
          '& > span': {
            marginBlockEnd: '-1',
          },
        },
        branchIndentGuide: {
          height: '100%',
          width: '1',
          background: 'border/regular',
          position: 'absolute',
          insetBlockStart: 0,
          '--tree-depth': 'calc(var(--depth) - 1)',
          '--tree-indentation-offset': 'calc(var(--tree-indentation) * var(--tree-depth))',
          '--tree-offset': 'calc(var(--tree-padding-inline-start) + var(--tree-indentation-offset))',
          '--tree-icon-offset': 'calc(var(--tree-icon-size) * 0.5 * var(--depth))',
          insetInlineStart: 'calc(var(--tree-offset) + var(--tree-icon-offset))',
          zIndex: 1,
        },
      },
      navigation: {
        branchControl: {
          borderRadius: 0,
          paddingInlineEnd: '16',
          '&:not([data-path="0"])::before': {
            content: '""',
            position: 'absolute',
            insetBlockStart: 0,
            insetInlineStart: 'var(--tree-offset)',
            insetInlineEnd: 0,
            height: '1',
            background: 'border/minimal',
          },
          _selected: {
            '&::after': {
              content: '""',
              position: 'absolute',
              insetBlock: 0,
              insetInlineEnd: 0,
              width: '4',
              background: 'border/selected',
              borderRadius: '0 2px 2px 0',
            },
          },
        },
        item: {
          borderRadius: 0,
          paddingInlineEnd: '16',
          '&:not([data-path="0"])::before': {
            content: '""',
            position: 'absolute',
            insetBlockStart: 0,
            insetInlineStart: 'var(--tree-offset)',
            insetInlineEnd: 0,
            height: '1',
            background: 'border/minimal',
          },
          '& > svg': {
            marginBlockStart: rem(9),
          },
          _selected: {
            '&::after': {
              content: '""',
              position: 'absolute',
              insetBlock: 0,
              insetInlineEnd: 0,
              width: '4',
              background: 'border/selected',
              borderRadius: '0 2px 2px 0',
            },
          },
        },
        branchIndicator: {
          marginBlockStart: '8',
          transformOrigin: 'center',
          transitionDuration: 'normal',
          transitionProperty: 'transform',
          transitionTimingFunction: 'default',
          _open: {
            transform: 'rotate(90deg)',
          },
        },
        branchText: {
          paddingBlock: '8',
          gap: '4',
          _open: {
            textStyle: 'body/md/semibold',
            color: 'text/primary',
          },
        },
        itemText: {
          paddingBlock: '8',
        },
        suffixGroup: {
          height: rem(35),
          '& > span': {
            marginBlockEnd: '-1',
          },
        },
        branchIndentGuide: {
          display: 'none',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'files',
  },
});

export default treeViewSlotRecipe;
